import React, { useState, useEffect, useContext } from 'react';
import { Modal, Button, Form, Alert, ListGroup, InputGroup, FormControl, Dropdown } from 'react-bootstrap';
import apiClient from '../api/axios';
import { toast } from 'react-toastify';
import { useAuth } from '../contexts/AuthContext';
import ConfirmationModal from './ConfirmationModal'; // Import the custom modal

// Custom toggle for the actions dropdown
const CustomToggle = React.forwardRef(({ children, onClick }, ref) => (
    <button
      ref={ref}
      onClick={(e) => {
        e.preventDefault();
        onClick(e);
      }}
      className="btn btn-link text-dark p-3 border-0"
    >
      {children}
    </button>
));

const DepartmentsManagerModal = ({ show, handleClose, onUpdate }) => {
    const { user } = useAuth();
    const [departments, setDepartments] = useState([]);
    const [newDepartmentName, setNewDepartmentName] = useState('');
    const [editingDepartment, setEditingDepartment] = useState(null); // { id, name }
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // State for the delete confirmation modal
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteModalContent, setDeleteModalContent] = useState({ title: '', message: '', onConfirm: null });

    const fetchDepartments = async () => {
        if (!user?.organization_id) return;
        setLoading(true);
        try {
            const response = await apiClient.get(`/departments/organization/${user.organization_id}`);
            setDepartments(response.data);
        } catch (err) {
            const errorMessage = 'Ошибка при загрузке списка направлений.';
            setError(errorMessage);
            toast.error(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (show) {
            fetchDepartments();
        }
    }, [show, user?.organization_id]);

    const handleCreate = async (e) => {
        e.preventDefault();
        if (!newDepartmentName.trim()) return;

        try {
            await apiClient.post('/departments/', { name: newDepartmentName });
            toast.success(`Направление "${newDepartmentName}" успешно создано.`);
            setNewDepartmentName('');
            fetchDepartments(); // Refresh list
            if(onUpdate) onUpdate();
        } catch (err) {
            const errorMessage = err.response?.data?.detail || 'Ошибка при создании направления.';
            toast.error(errorMessage);
        }
    };

    const handleDeleteClick = (dept) => {
        setDeleteModalContent({
            title: 'Подтвердить удаление',
            message: `Вы уверены, что хотите удалить направление "${dept.name}"? Это действие нельзя будет отменить.`,
            onConfirm: () => handleConfirmDelete(dept.id),
        });
        setShowDeleteModal(true);
    };

    const handleConfirmDelete = async (departmentId) => {
        try {
            await apiClient.delete(`/departments/${departmentId}`);
            toast.success('Направление успешно удалено.');
            fetchDepartments(); // Refresh list
            if(onUpdate) onUpdate();
        } catch (err) {
            const errorMessage = err.response?.data?.detail || 'Ошибка при удалении направления.';
            toast.error(errorMessage);
        } finally {
            setShowDeleteModal(false);
        }
    };

    const handleUpdate = async () => {
        if (!editingDepartment || !editingDepartment.name.trim()) return;

        try {
            await apiClient.put(`/departments/${editingDepartment.id}`, { name: editingDepartment.name });
            toast.success('Направление успешно обновлено.');
            setEditingDepartment(null);
            fetchDepartments(); // Refresh list
            if(onUpdate) onUpdate();
        } catch (err) {
            const errorMessage = err.response?.data?.detail || 'Ошибка при обновлении направления.';
            toast.error(errorMessage);
        }
    };

    const renderDepartmentItem = (dept) => {
        const isEditing = editingDepartment && editingDepartment.id === dept.id;

        return (
            <ListGroup.Item key={dept.id} className="d-flex justify-content-between align-items-center">
                {isEditing ? (
                    <InputGroup>
                        <FormControl
                            value={editingDepartment.name}
                            onChange={(e) => setEditingDepartment({ ...editingDepartment, name: e.target.value })}
                            autoFocus
                        />
                        <Button variant="success" size="sm" onClick={handleUpdate}>Сохранить</Button>
                        <Button variant="secondary" size="sm" onClick={() => setEditingDepartment(null)}>Отмена</Button>
                    </InputGroup>
                ) : (
                    <>
                        {dept.name}
                        <Dropdown>
                            <Dropdown.Toggle as={CustomToggle} id={`dropdown-actions-${dept.id}`}>
                                <i className="fas fa-ellipsis-v"></i>
                            </Dropdown.Toggle>
                    
                            <Dropdown.Menu>
                                <Dropdown.Item onClick={() => setEditingDepartment({ id: dept.id, name: dept.name })}>Изменить</Dropdown.Item>
                                <Dropdown.Item onClick={() => handleDeleteClick(dept)} className="text-danger">Удалить</Dropdown.Item>
                            </Dropdown.Menu>
                        </Dropdown>
                    </>
                )}
            </ListGroup.Item>
        );
    };

    return (
        <>
            <Modal show={show} onHide={handleClose} centered size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Управление направлениями</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {error && <Alert variant="danger">{error}</Alert>}

                    <h5>Добавить новое направление</h5>
                    <Form onSubmit={handleCreate} className="mb-4">
                        <InputGroup>
                            <FormControl
                                placeholder="Название направления (напр. Музыканты)"
                                value={newDepartmentName}
                                onChange={(e) => setNewDepartmentName(e.target.value)}
                            />
                            <Button variant="primary" type="submit">Добавить</Button>
                        </InputGroup>
                    </Form>

                    <h5>Существующие направления</h5>
                    {loading ? (
                        <p>Загрузка...</p>
                    ) : (
                        <ListGroup>
                            {departments.length > 0 ? departments.map(renderDepartmentItem) : <p>Направлений пока нет.</p>}
                        </ListGroup>
                    )}
                </Modal.Body>
            </Modal>

            <ConfirmationModal 
                show={showDeleteModal}
                title={deleteModalContent.title}
                message={deleteModalContent.message}
                onConfirm={deleteModalContent.onConfirm}
                onCancel={() => setShowDeleteModal(false)}
                confirmText="Удалить"
                cancelText="Отмена"
                variant="danger"
            />
        </>
    );
};

export default DepartmentsManagerModal;
